import { useState } from 'react';
import {
  PaperPlaneTiltIcon,
  PlusIcon,
  SpinnerIcon,
} from '@phosphor-icons/react';
import { T, Var, useGT } from 'gt-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RecipientRow } from './RecipientRow';
import { ContactRow } from './ContactRow';
import type {
  CustomRecipient,
  EmailComposerContact,
  RecipientChipDescriptor,
  RecipientRole,
  SendFromOption,
} from './types';

interface EmailRecipientsPanelProps {
  /** Contacts to surface in the right-hand panel. */
  contacts: EmailComposerContact[];
  contactsLoading?: boolean;

  /** Contact emails the user has assigned a role to. */
  selectedEmails: Map<string, RecipientRole>;
  /** Ad-hoc addresses entered inline. */
  customRecipients: CustomRecipient[];

  /**
   * Validate + persist a custom recipient. Return `false` to surface an inline
   * error (the panel displays a red-tint on the input until next keystroke).
   */
  addCustomRecipient: (email: string, role: RecipientRole) => boolean;
  addContactToRole: (email: string, role: RecipientRole) => void;
  removeCustomRecipient: (id: string) => void;
  removeContact: (email: string) => void;
  toggleEmail: (email: string) => void;
  toggleEmailRole: (email: string) => void;

  /** Optional "BCC me" affordance — requires the sender's own email. */
  bccSender?: boolean;
  setBccSender?: (b: boolean) => void;
  senderEmail?: string | null;

  /** Optional Send-From selector. Omit to hide. */
  sendFromOptions?: SendFromOption[];
  selectedSendFromKey?: string;
  setSelectedSendFromKey?: (k: string) => void;
}

/**
 * Two-column recipients editor:
 * - LEFT: TO / CC / BCC chip rows with inline email entry + autocomplete
 *   against the contact list, plus an optional Send From selector.
 * - RIGHT: scrollable contact list with role-toggle pills + checkboxes.
 *
 * Controlled: the parent owns `selectedEmails`, `customRecipients`, and
 * `bccSender`. Transient inline-add UI state lives inside the panel.
 */
export function EmailRecipientsPanel(props: EmailRecipientsPanelProps) {
  const {
    contacts,
    contactsLoading = false,
    selectedEmails,
    customRecipients,
    addCustomRecipient,
    addContactToRole,
    removeCustomRecipient,
    removeContact,
    toggleEmail,
    toggleEmailRole,
    bccSender = false,
    setBccSender,
    senderEmail,
    sendFromOptions,
    selectedSendFromKey,
    setSelectedSendFromKey,
  } = props;

  const gt = useGT();

  // Transient inline-add state — kept internal to the panel.
  const [addingRole, setAddingRole] = useState<RecipientRole | null>(null);
  const [addingValue, setAddingValue] = useState('');
  const [addingError, setAddingError] = useState(false);

  const recipientCount =
    selectedEmails.size +
    customRecipients.length +
    (bccSender && senderEmail ? 1 : 0);

  function buildChipsForRole(role: RecipientRole): RecipientChipDescriptor[] {
    const chips: RecipientChipDescriptor[] = [];
    for (const [email, r] of selectedEmails) {
      if (r !== role) continue;
      chips.push({
        key: `c-${email}`,
        email,
        onRemove: () => removeContact(email),
      });
    }
    for (const r of customRecipients) {
      if (r.role !== role) continue;
      chips.push({
        key: `cr-${r.id}`,
        email: r.email,
        onRemove: () => removeCustomRecipient(r.id),
      });
    }
    return chips;
  }

  const toChips = buildChipsForRole('to');
  const ccChips = buildChipsForRole('cc');
  // BCC chips include the sender-self chip (when toggled) so the user sees a
  // single unified list of BCC addresses regardless of source.
  const bccChips: RecipientChipDescriptor[] = [
    ...(bccSender && senderEmail && setBccSender
      ? [
          {
            key: 'bcc-self',
            email: senderEmail,
            onRemove: () => setBccSender(false),
          },
        ]
      : []),
    ...buildChipsForRole('bcc'),
  ];

  function commitAdd() {
    if (addingRole == null) return;
    const ok = addCustomRecipient(addingValue, addingRole);
    if (ok) {
      setAddingValue('');
      setAddingRole(null);
      setAddingError(false);
    } else {
      setAddingError(true);
    }
  }

  function cancelAdd() {
    setAddingRole(null);
    setAddingValue('');
    setAddingError(false);
  }

  function openAddFor(role: RecipientRole) {
    setAddingRole(role);
    setAddingValue('');
    setAddingError(false);
  }

  // Build the autocomplete pool from contacts that aren't already assigned to
  // a role and aren't duplicated by a custom recipient. All comparisons use
  // a lower-cased view so mixed-case duplicates collapse correctly.
  const selectedLower = new Set(
    Array.from(selectedEmails.keys(), (e) => e.trim().toLowerCase()),
  );
  const customLower = new Set(
    customRecipients.map((r) => r.email.trim().toLowerCase()),
  );
  const suggestionPool = contacts.filter((c) => {
    const key = c.email.trim().toLowerCase();
    return !selectedLower.has(key) && !customLower.has(key);
  });
  const query = addingValue.trim().toLowerCase();
  const suggestions = suggestionPool
    .filter((c) => query === '' || c.email.toLowerCase().includes(query))
    .slice(0, 8);

  function pickSuggestion(email: string) {
    if (addingRole == null) return;
    addContactToRole(email, addingRole);
    setAddingValue('');
    setAddingRole(null);
    setAddingError(false);
  }

  const canShowSendFrom =
    sendFromOptions != null &&
    sendFromOptions.length > 0 &&
    selectedSendFromKey != null &&
    setSelectedSendFromKey != null;

  const canBccSelf =
    setBccSender != null &&
    !bccSender &&
    senderEmail != null &&
    senderEmail !== '';

  return (
    <div className="grid grid-cols-1 gap-4 px-1 lg:grid-cols-2 lg:gap-6">
      {/* ─── Left: Recipients ────────────────────────────── */}
      <section className="space-y-3">
        <div className="flex items-center gap-2 text-sm font-medium">
          <PaperPlaneTiltIcon className="h-4 w-4" />
          <T context="email composer section heading, recipients">Recipients</T>
          <span className="ml-auto rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {recipientCount}
          </span>
        </div>

        <RecipientRow
          label={gt('TO', { context: 'email recipient role label' })}
          chips={toChips}
          isAdding={addingRole === 'to'}
          addingValue={addingValue}
          addingError={addingError}
          suggestions={addingRole === 'to' ? suggestions : []}
          onChangeAdding={(v) => {
            setAddingValue(v);
            setAddingError(false);
          }}
          onCommitAdd={commitAdd}
          onCancelAdd={cancelAdd}
          onClickAdd={() => openAddFor('to')}
          onPickSuggestion={pickSuggestion}
        />
        <RecipientRow
          label={gt('CC', { context: 'email carbon copy role label' })}
          chips={ccChips}
          isAdding={addingRole === 'cc'}
          addingValue={addingValue}
          addingError={addingError}
          suggestions={addingRole === 'cc' ? suggestions : []}
          onChangeAdding={(v) => {
            setAddingValue(v);
            setAddingError(false);
          }}
          onCommitAdd={commitAdd}
          onCancelAdd={cancelAdd}
          onClickAdd={() => openAddFor('cc')}
          onPickSuggestion={pickSuggestion}
        />
        <RecipientRow
          label={gt('BCC', { context: 'email blind carbon copy role label' })}
          chips={bccChips}
          isAdding={addingRole === 'bcc'}
          addingValue={addingValue}
          addingError={addingError}
          suggestions={addingRole === 'bcc' ? suggestions : []}
          onChangeAdding={(v) => {
            setAddingValue(v);
            setAddingError(false);
          }}
          onCommitAdd={commitAdd}
          onCancelAdd={cancelAdd}
          onClickAdd={() => openAddFor('bcc')}
          onPickSuggestion={pickSuggestion}
          extraAction={
            canBccSelf ? (
              <button
                type="button"
                onClick={() => setBccSender?.(true)}
                className="inline-flex items-center gap-0.5 rounded px-1.5 py-0.5 text-xs font-medium text-muted-foreground hover:bg-muted cursor-pointer"
              >
                <PlusIcon className="h-3 w-3" />
                <T context="bcc row, button to BCC the sender on outgoing email">
                  BCC me
                </T>
              </button>
            ) : null
          }
        />
        {canShowSendFrom && (
          <div className="flex items-center gap-2 pt-1 text-sm">
            <span className="shrink-0 text-xs font-medium text-muted-foreground">
              <T context="email composer, sender mailbox selector label">
                Send From:
              </T>
            </span>
            <Select
              value={selectedSendFromKey}
              onValueChange={setSelectedSendFromKey}
            >
              <SelectTrigger className="h-8 flex-1 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {sendFromOptions.map((option) => (
                  <SelectItem key={option.key} value={option.key}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
      </section>

      {/* ─── Right: Contacts ─────────────────────────────── */}
      <section className="space-y-2">
        <div className="flex items-center gap-2 text-sm font-medium">
          <T context="email composer, contacts panel heading">Contacts</T>
          <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground">
            {contacts.length}
          </span>
          <span className="ml-auto text-xs text-muted-foreground">
            <T context="email composer, contacts hint to assign role">
              Click <Var>TO</Var>/<Var>CC</Var> to assign
            </T>
          </span>
        </div>
        {contactsLoading ? (
          <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
            <SpinnerIcon className="h-4 w-4 animate-spin" />
            <T context="loading state">Loading contacts...</T>
          </div>
        ) : contacts.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            <T context="email composer, no email contacts found">
              No email contacts found. Use the TO/CC rows on the left to add
              ad-hoc addresses.
            </T>
          </p>
        ) : (
          <div className="divide-y overflow-y-auto rounded-md border max-h-[240px] min-h-[200px]">
            {contacts.map((contact) => {
              const role = selectedEmails.get(contact.email);
              return (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  role={role}
                  onToggle={() => toggleEmail(contact.email)}
                  onToggleRole={() => toggleEmailRole(contact.email)}
                />
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}
